import React, { useEffect, useMemo, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { PlusCircle } from "lucide-react";
import { isAxiosError } from "axios";

import { UserInfoEditor } from "./UserInfoEditor";
import { UserModalBreedFields } from "./UserModalBreedFields";
import { ResponsiveModalSheet } from "./ResponsiveModalSheet";
import { Button, ButtonProps } from "./ui";

import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { useStateScopedBreeds, UseStateScopedBreedsOptions } from "@/shared/hooks/useStateScopedBreeds";
import { getPostalCodeVerdict } from "@/shared/hooks/useStateFromZipcode";
import { useFormParentContext } from "../contexts/FormParent";

import { UsPolicyStepSchema } from "@/quote-ptz-us/src/schema/PtzUsQuote";
import { PolicyStepSchema } from "@/quote-ptz-ca/src/schema/PtzCaQuote";
import { Quote } from "../types/Quote.interface";
import Strings from "../utils/Strings.constants";
import { PublicConfig } from "../PublicConfig";
import { PRIORITY_CODE_DEPRECATED, SpotErrorMessage } from "../types/SpotAPI";
import ErrorUtils from "../utils/ErrorUtils";
import { EmailUtils } from "../utils/EmailUtils";
import { BreedUtils, BreedsCatalog } from "../utils/BreedUtils";
import { PlanFilterUtils } from "../utils/PlanFilterUtils";
import { GiftCardUtils } from "../utils/GiftCardUtils";
import { cn } from "../utils";

type FloatingVariant = "floating" | "floating-inner";

export type UserModalConfig = {
    stepSchema: typeof UsPolicyStepSchema | typeof PolicyStepSchema;
    floatingVariant?: FloatingVariant;
    zipField: {
        label: string;
        placeholder?: string;
        mask: (value: string) => string;
        showGiftCardWrapper: boolean;
        /** Statsig - zip code autocomplete test (ENG-16887). The autocomplete UI only. */
        validateZipCode?: boolean;
        /** Block Save on a zip Spot confirms doesn't exist, and hold /rewards/eligible until it's confirmed. */
        requireVerifiedZip?: boolean;
    };
    emailField?: { label: string; placeholder?: string };
    firstNameField?: { label: string; placeholder?: string };
    lastNameField?: { label: string; placeholder?: string };
    formStyles?: {
        wrapper?: string;
        fields?: {
            firstName?: string;
            lastName?: string;
            email?: string;
            zipCode?: string;
        };
    };
    triggerLabel?: string;
};

type UserModalProps = {
    isOpen: boolean;
    onClose: () => void;
    config: UserModalConfig;
    triggerButton?: React.ReactElement<ButtonProps>;
};

/**
 * Fails any policy whose stored breed the resolved state doesn't offer, so Save can't submit the
 * combination that strands the Plan step. The step schema alone can't catch it: the breedID is a
 * perfectly valid non-empty string — it's only wrong relative to the zip the customer just typed.
 */
const withUnofferedBreedCheck = (stepSchema: z.ZodTypeAny, catalog?: BreedsCatalog): z.ZodTypeAny => {
    if (!catalog) return stepSchema;

    return stepSchema.superRefine((values, ctx) => {
        const policies = (values as { policies?: ({ breedID?: string } | undefined)[] })?.policies;
        BreedUtils.getUnofferedBreedIndexes(policies, catalog).forEach(index => {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["policies", index, "breedID"],
                message: Strings.ERRORS.BREED_UNAVAILABLE_IN_STATE
            });
        });
    });
};

const UserModalForm = ({ onClose, config }: Pick<UserModalProps, "onClose" | "config">) => {
    const floatingVariant: FloatingVariant = config.floatingVariant ?? "floating";

    const {
        appState: { updateQuote, isQuoteUpdating, mockAPIError, userModalAccountExistsError, underwriter, debounceEntries, promotionalOffer, giftCardData },
        updateAppState
    } = useAppLayerContext();
    const { getValues: getParentValues, reset: resetParentForm } = useFormParentContext<Quote>();

    const queryClient = useQueryClient();
    const emailUtils = useMemo(() => (underwriter ? new EmailUtils(underwriter, queryClient) : null), [underwriter, queryClient]);

    type FormValues = z.infer<typeof config.stepSchema>;
    const parentValues = getParentValues();
    const defaultValues = {
        firstName: parentValues.firstName ?? "",
        lastName: parentValues.lastName ?? "",
        email: parentValues.email ?? "",
        ratingZipcode: parentValues.ratingZipcode ?? "",
        phone: parentValues.phone ?? "",
        policies: parentValues.policies ?? [],
        marketing: parentValues.marketing,
        extra: parentValues.extra
    } as unknown as FormValues;

    // The catalog is resolved from this very form (below), so the resolver reaches it through a ref
    // rather than a closure over a value that doesn't exist yet. Effects have flushed long before
    // any Save click, so the ref is never stale at validation time.
    const breedsCatalogRef = useRef<BreedsCatalog | undefined>(undefined);

    const form = useForm<FormValues>({
        resolver: (values, context, options) => zodResolver(withUnofferedBreedCheck(config.stepSchema, breedsCatalogRef.current))(values, context, options),
        defaultValues
    });

    const { handleSubmit, setError } = form;

    // Breed catalog for the state resolved from the zip *currently typed in this modal* (the hook
    // reads `ratingZipcode` off the form we hand it, not the parent's). Changing the zip to another
    // state can strand a breed that state doesn't offer; UserModalBreedFields clears it and asks
    // for a new one, and the empty breedID fails the resolver so Save can't submit until it's fixed.
    // The cast is safe and necessary: RHF's `watch` is invariant in the form type, and this form is
    // typed to the step schema (no `ratingAddress`). The hook only ever reads `ratingZipcode` and
    // `ratingAddress.*`; the latter simply resolve to undefined here, which the hook already treats
    // as "no persisted state to trust" and falls back to resolving state from the typed zip.
    const breedsCatalog = useStateScopedBreeds({ form: form as unknown as UseStateScopedBreedsOptions["form"], underwriter });

    useEffect(() => {
        breedsCatalogRef.current = breedsCatalog;
    }, [breedsCatalog]);

    // Clear the account-exists flag when the modal closes (UserModalForm unmounts via the
    // `{isOpen && ...}` guard below). Without this, closing without saving leaves the flag
    // set in appState, and re-opening shows a false-positive inline message until the user
    // retypes the email.
    useEffect(() => {
        return () => {
            updateAppState({ userModalAccountExistsError: false, isEmailSuggestionShowing: false });
        };
    }, [updateAppState]);

    // The zip field's gift card banner writes the offer for whatever zip is typed here into
    // appState, which the step's toast and disclaimer also read. Until Save commits that zip, the
    // quote still has the old one, so closing without saving a new zip puts its offer back.
    const [onOpen] = useState(() => ({ ratingZipcode: parentValues.ratingZipcode, giftCard: { promotionalOffer, giftCardData } }));
    useEffect(() => {
        return () => {
            if (getParentValues().ratingZipcode === onOpen.ratingZipcode) updateAppState(onOpen.giftCard);
        };
    }, [getParentValues, onOpen, updateAppState]);

    // Same key and request as useGiftCardZipInfo, so the banner's response is reused; fetched only
    // when the banner never got one. Any failure leaves no offer rather than the old zip's. A fetch
    // can outlive a later zip change, so it only writes while the quote still has this zip.
    const syncPromotionalOffer = async (quote: Quote) => {
        const rewardsQuote: Quote = { ...quote, ratingAddress: { zipCode: quote.ratingZipcode } };
        const offer = await queryClient
            .fetchQuery({
                queryKey: GiftCardUtils.createRewardsEligibilityQueryKey(rewardsQuote),
                queryFn: () => GiftCardUtils.getGiftCardEligibility(rewardsQuote),
                staleTime: Infinity
            })
            .then(
                eligibility => GiftCardUtils.getPromotionalOfferEligibility(eligibility),
                () => null
            );
        if (getParentValues().ratingZipcode === quote.ratingZipcode) {
            updateAppState({ promotionalOffer: offer ?? undefined });
        }
    };

    const onSubmit = async (data: FormValues) => {
        updateAppState({ userModalAccountExistsError: false });

        // Mirror step-1: validate the email for a likely typo before saving. When a typo is
        // detected, handleEmailDebounce populates appState.debounceEntries and returns false;
        // we abort the save so the "did you mean ...?" suggestion (rendered in UserInfoEditor)
        // can surface. The user accepts/dismisses it, then a second save proceeds.
        const userEmail = (data as unknown as { email?: string }).email;
        if (emailUtils) {
            const noTypoSuggestion = await emailUtils.handleEmailDebounce({
                email: userEmail,
                debounceEntries,
                updateAppState
            });
            if (!noTypoSuggestion) return;
        }

        // The API's invalid-postal-code 400 lands in appState.asyncErrors, which nothing on the
        // coverage step consumes, so without this check the modal sits open with no message.
        // FAIL OPEN: only a definitive "invalid" blocks; "unknown" (network failure, 5xx, timeout)
        // lets the API decide. setError is safe here, unlike step 1: no PolicyErrorController
        // watches this form.
        const editedZip = (data as unknown as { ratingZipcode?: string }).ratingZipcode;
        if (config.zipField.requireVerifiedZip && editedZip) {
            const verdict = await getPostalCodeVerdict(queryClient, editedZip);
            if (verdict.status === "invalid") {
                setError("ratingZipcode" as Parameters<typeof setError>[0], { type: "manual", message: Strings.ERRORS.ZIP_INVALID });
                return;
            }
        }

        try {
            const currentValues = getParentValues();
            const userFields = data as unknown as {
                firstName?: string;
                lastName?: string;
                email?: string;
                ratingZipcode?: string;
                phone?: string;
            };
            // Policies come from the modal's own form, not the parent: a zip change here can force
            // a breed re-selection (UserModalBreedFields), and that correction only exists on this
            // form. Taking the parent's policies would send the stale, state-unavailable breedID
            // back to the API — the exact combination that strands the Plan step.
            const editedPolicies = (data as unknown as { policies?: Quote["policies"] }).policies;
            const ratingZipcode = userFields.ratingZipcode ?? currentValues.ratingZipcode;

            // ENG-20331: a zip moved into CA must not carry CA-forbidden coverage into the PUT — the prod
            // underwriter rejects it with a 500. Same state resolution as the Policy step (formConfig):
            // trust the persisted state only while the zip still matches it. An "unknown" verdict skips
            // the remap and lets the API decide.
            const zipChanged = !!ratingZipcode && ratingZipcode !== currentValues.ratingAddress?.zipCode;
            const postalCode = (zipChanged || !currentValues.ratingAddress?.state) && ratingZipcode ? await getPostalCodeVerdict(queryClient, ratingZipcode) : undefined;
            const ratingState =
                !zipChanged && currentValues.ratingAddress?.state ? currentValues.ratingAddress.state : postalCode?.status === "valid" ? postalCode.state?.abbreviation : undefined;

            const updatedData: Quote = {
                ...currentValues,
                firstName: userFields.firstName ?? currentValues.firstName,
                lastName: userFields.lastName ?? currentValues.lastName,
                email: userFields.email ?? currentValues.email,
                ratingZipcode,
                phone: userFields.phone ?? currentValues.phone,
                policies: PlanFilterUtils.remapPoliciesForState(editedPolicies ?? currentValues.policies, ratingState)
            };

            if (!updateQuote) {
                throw new Error("updateQuote is not defined");
            }

            const onUpdateQuote = async () => {
                try {
                    return await updateQuote.mutateAsync({ quote: updatedData, updateType: "UPDATE" });
                } catch (error) {
                    const hasPcodeError = ErrorUtils.hasSpotError(error, PRIORITY_CODE_DEPRECATED);
                    if (hasPcodeError) {
                        let retryData = { ...updatedData, affiliateCode: "SPOT", discountCode: "SPOT" };
                        if (PublicConfig.ENVIRONMENT === "development" && mockAPIError) {
                            retryData = { ...retryData, extra: { ...retryData.extra, sim: "" } };
                        }
                        return await updateQuote.mutateAsync({ quote: retryData, updateType: "UPDATE" });
                    }
                    throw error;
                }
            };

            const updatedQuote = await onUpdateQuote();
            if (updatedQuote) {
                resetParentForm(updatedQuote);
                // The modal may have been closed mid-Save, which put the old zip's offer back, so
                // the saved zip's offer is written from here rather than by the banner. Not awaited:
                // it never holds the modal open.
                if (zipChanged) void syncPromotionalOffer(updatedQuote);
            }
            onClose();
        } catch (error) {
            console.error("Failed to update user info:", error);
            if (isAxiosError(error) && Array.isArray(error.response?.data?.error?.message)) {
                const apiErrors = error.response.data.error.message as SpotErrorMessage[];
                const accountExists = apiErrors.some(e => e.id === "account-exists");
                const otherErrors = apiErrors.filter(e => e.id !== "account-exists");

                if (accountExists) updateAppState({ userModalAccountExistsError: true });
                if (otherErrors.length > 0) updateAppState({ asyncErrors: otherErrors });
            } else {
                updateAppState({ hasUnknownError: true });
            }
        }
    };

    return (
        <FormProvider {...form}>
            <UserInfoEditor
                config={{
                    stepSchema: config.stepSchema,
                    floatingVariant,
                    zipField: config.zipField,
                    emailField: config.emailField,
                    firstNameField: config.firstNameField,
                    lastNameField: config.lastNameField
                }}
                styles={config.formStyles}
                showAccountExistsError={!!userModalAccountExistsError}
                onClearAccountExistsError={() => updateAppState({ userModalAccountExistsError: false })}
                // Matches the zip's grid placement: same `order-*` (so it lands right beneath it,
                // DOM order breaking the tie) and the same full-width span.
                afterZipSlot={<UserModalBreedFields catalog={breedsCatalog} floatingVariant={floatingVariant} className={cn("col-span-2", config.formStyles?.fields?.zipCode)} />}
            />
            <div className="mt-3 flex w-full flex-col gap-4">
                <Button
                    loading={isQuoteUpdating}
                    // Blocked while the new zip's catalog is still in flight: an unsettled catalog
                    // hasn't yet had the chance to invalidate a breed, so saving into that window
                    // would submit the stale breed and strand the Plan step anyway.
                    disabled={!!userModalAccountExistsError || breedsCatalog.status === "unsettled"}
                    variant={floatingVariant === "floating-inner" ? "default-rounded" : "default"}
                    onClick={handleSubmit(onSubmit)}
                    className="w-full"
                    startDecorator={<PlusCircle className="size-5" />}
                >
                    {Strings.SAVE}
                </Button>
            </div>
        </FormProvider>
    );
};

export function UserModal({ isOpen, onClose, config, triggerButton }: UserModalProps) {
    return (
        <ResponsiveModalSheet
            isOpen={isOpen}
            onClose={onClose}
            title={<span className="text-lg font-bold text-content-primary">Edit your info</span>}
            sheetSide="top"
            triggerButton={triggerButton !== undefined ? React.cloneElement(triggerButton) : undefined}
            showCloseButton
            styles={{
                sheet: {
                    content: "flex flex-col",
                    title: "flex justify-between items-center"
                },
                modal: {
                    title: "flex justify-between items-center",
                    content: "flex flex-col !max-w-[360px]",
                    closeButton: "hidden"
                }
            }}
        >
            {isOpen && <UserModalForm onClose={onClose} config={config} />}
        </ResponsiveModalSheet>
    );
}

export default UserModal;
